import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router";
import PlannedTimeChanges from "../consolidation/PlannedTimeChanges.jsx";
import { dayWords } from "../../utils/billingPeriods.js";
import { withInvoiceReturn } from "../../utils/consolidationOrderNavigation.js";
import { describeGroup, joinLabels, routeName } from "../../utils/dispatchChangeStory.js";

const TITLE = "mb-0.5 text-[10px] font-extrabold uppercase tracking-wide text-slate-500";
const dayLabel = (value, options) => new Intl.DateTimeFormat("fr-CA", { ...options, timeZone: "UTC" }).format(new Date(`${value}T12:00:00Z`));

function ChangedRoute({ group, billedOrders, timezone }) {
  const story = describeGroup(group, timezone);
  const routes = (group.routes || []).map(routeName).filter(Boolean);
  const orders = (group.order_labels || []).filter((label) => billedOrders.has(label));
  // Heures prévues seulement : accepter garde toutes les heures saisies, donc le montant facturé ne bouge pas.
  const timesOnly = story.times.length === group.changes.length;
  return <div className="grid gap-1">
    <p><strong>{joinLabels(routes) || group.label}</strong> — {orders.length
      ? <>tournée qui contient <strong>{joinLabels(orders)}</strong> de ce brouillon.</> : "tournée facturée dans ce brouillon."}</p>
    {timesOnly ? null : <p>{story.moves.map((move) => `${move.text} : ${move.from} ➜ ${move.to}`).concat(story.sentences).join(" · ")}</p>}
    {story.times.length ? <PlannedTimeChanges times={story.times} timezone={timezone} /> : null}
    {story.losses.length ? <ul aria-label="En acceptant" className="list-disc pl-4 font-semibold text-[#9a3412]">{story.losses.map((text) => <li key={text}>{text}</li>)}</ul>
      : timesOnly ? <p><strong className="text-accent">Montant inchangé :</strong> seules les heures prévues ont bougé ; les heures saisies sont conservées.</p> : null}
  </div>;
}

// « Des corrections du Dispatch… » ne disait ni la journée ni la tournée : les nommer, avec la marche à suivre.
function DispatchReview({ reason, billedOrders, invoiceId }) {
  const timezone = reason.timezone || "America/Toronto";
  const day = dayLabel(reason.dispatch_date, { day: "numeric", month: "short" });
  const dependencies = [...new Set(reason.groups.flatMap((group) => group.dependencies || []))].map((value) => dayLabel(value, { day: "numeric", month: "short" }));
  return <section role="alert" className="mb-2 rounded-[4px] border border-slate-300 border-l-4 border-l-[#c5524a] bg-white text-xs text-slate-900">
    <p className="border-b border-slate-200 px-2.5 py-1.5 text-[13px] font-extrabold">
      Envoi bloqué : le Dispatch du {dayLabel(reason.dispatch_date, { weekday: "long", day: "numeric", month: "long" })} a changé après la validation des relevés</p>
    <div className="grid gap-x-4 gap-y-2 px-2.5 py-1.5 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="grid gap-2">
        <p className={TITLE}>Ce qui touche ce brouillon</p>
        {reason.groups.map((group) => <ChangedRoute key={group.id} group={group} billedOrders={billedOrders} timezone={timezone} />)}
      </div>
      <div>
        <p className={TITLE}>Pour débloquer</p>
        <ol className="list-decimal space-y-0.5 pl-4">
          <li>Ouvrez les changements du {day} dans Consolidation.</li>
          <li>{dependencies.length ? `Traitez d’abord la journée du ${joinLabels(dependencies)}, puis acceptez la modification.`
            : reason.groups.some((group) => group.legacy) ? "Ancienne journée : utilisez « Recommencer toute la consolidation » (Actions avancées, en haut de la journée)."
              : "Cliquez « Accepter la modification » : la modification est prête."}</li>
          <li>Cliquez « Retour au relevé » : ce brouillon se rouvre et il est revérifié tout seul.</li>
        </ol>
      </div>
    </div>
    {/* Même onglet : la connexion est propre à chaque onglet, un nouvel onglet la redemanderait. */}
    <div className="flex justify-end border-t border-slate-200 px-2.5 py-1.5">
      <Link className="inline-flex h-7 items-center gap-1.5 rounded-[4px] border border-accent bg-accent px-2.5 font-extrabold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        to={withInvoiceReturn(reason.url, invoiceId)}>Ouvrir les changements du {day}<ArrowRight size={13} aria-hidden="true" /></Link>
    </div>
  </section>;
}

/** Pourquoi un brouillon ne peut pas partir : les revues du Dispatch en détail, les autres raisons en liste. */
export default function InvoiceBlockingReasons({ reasons, lines = [], invoiceId }) {
  const billedOrders = new Set(lines.map((line) => line.order_reference).filter(Boolean));
  const reviews = reasons.filter((reason) => reason.code === "dispatch_review_required" && reason.dispatch_date && reason.groups?.length);
  const others = reasons.filter((reason) => !reviews.includes(reason));
  return <>
    {reviews.map((reason) => <DispatchReview key={reason.dispatch_date} reason={reason} billedOrders={billedOrders} invoiceId={invoiceId} />)}
    {others.length || !reviews.length ? <div className="mb-2 rounded-[4px] border border-slate-300 border-l-4 border-l-[#c5524a] bg-white px-2.5 py-1.5 text-xs text-slate-900"><strong>Envoi bloqué : corrigez la cause ci-dessous.</strong>
      {others.length ? <ul className="mt-1 list-disc space-y-0.5 pl-5">{others.map((reason, index) => <li key={`${reason.code || "reason"}-${index}`}>
        {reason.message || "La commande source ne peut pas être facturée."}
        {reason.url ? <> <Link className="font-bold text-accent underline" to={withInvoiceReturn(reason.url, invoiceId)}>Ouvrir Consolidation du {dayWords(reason.date || reason.dispatch_date)}</Link></>
          : reason.dispatch_date ? ` Journée du ${dayLabel(reason.dispatch_date, { day: "numeric", month: "long" })}.` : null}
      </li>)}</ul> : <p className="mt-0.5">La commande source n’est plus admissible à la facturation.</p>}
    </div> : null}
  </>;
}
